import { useEffect, useState } from "react";
import { Save, Undo2, Plus, Trash2, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [showSecret, setShowSecret] = useState(false);

  const load = () => api.get("/admin/site-settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);

  if (!s) return null;

  const save = async () => {
    try {
      const payload = { ...s };
      delete payload.previous_hero_image;
      await api.put("/admin/site-settings", payload);
      toast.success("Settings saved");
      load();
    } catch (e) { toast.error("Save failed"); }
  };

  const undoHero = async () => {
    try { await api.post("/admin/site-settings/undo-hero"); toast.success("Hero restored"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nothing to restore"); }
  };

  const updSection = (i, key, val) => {
    const arr = [...(s.how_its_made_sections || [])];
    arr[i] = { ...arr[i], [key]: val };
    setS({ ...s, how_its_made_sections: arr });
  };
  const addSection = () => setS({ ...s, how_its_made_sections: [...(s.how_its_made_sections || []), { eyebrow: "", title: "", body: "", image: "" }] });
  const removeSection = (i) => {
    const arr = [...(s.how_its_made_sections || [])]; arr.splice(i, 1);
    setS({ ...s, how_its_made_sections: arr });
  };

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-brand-soft">Site settings</h1>
        <p className="text-sm text-brand-soft/60 mt-1">Customize the hero, community links, payment gateway and editorial story.</p>
      </div>

      {/* Hero */}
      <div className="bg-white rounded-2xl border border-brand-maroon/10 p-6 sm:p-8 space-y-5">
        <h2 className="font-serif text-xl text-brand-soft">Hero</h2>
        <ImageUploader label="Hero image" recommended="(recommended 1600×900)" value={s.hero_image} onChange={(v) => setS({ ...s, hero_image: v })} />
        {s.previous_hero_image && <button onClick={undoHero} className="text-xs inline-flex items-center gap-1 text-brand-maroon"><Undo2 className="w-3 h-3" /> Restore previous hero</button>}
        <Input label="Tagline" value={s.hero_tagline} onChange={(v) => setS({ ...s, hero_tagline: v })} />
        <Input label="Subtitle" value={s.hero_subtitle} onChange={(v) => setS({ ...s, hero_subtitle: v })} />
      </div>

      {/* Community Links */}
      <div className="bg-white rounded-2xl border border-brand-maroon/10 p-6 sm:p-8 space-y-4 mt-6">
        <h2 className="font-serif text-xl text-brand-soft">Community links</h2>
        <Input label="Telegram channel link" value={s.telegram_link} onChange={(v) => setS({ ...s, telegram_link: v })} placeholder="https://t.me/..." testId="settings-telegram" />
        <Input label="WhatsApp channel link" value={s.whatsapp_link} onChange={(v) => setS({ ...s, whatsapp_link: v })} placeholder="https://whatsapp.com/channel/..." testId="settings-whatsapp" />
        <Input label="Instagram link" value={s.instagram_link} onChange={(v) => setS({ ...s, instagram_link: v })} />
        <Input label="Contact email" value={s.contact_email} onChange={(v) => setS({ ...s, contact_email: v })} />
      </div>

      {/* Razorpay */}
      <div className="bg-white rounded-2xl border border-brand-maroon/10 p-6 sm:p-8 space-y-4 mt-6">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl text-brand-soft inline-flex items-center gap-2">
            <Lock className="w-4 h-4 text-brand-maroon" /> Razorpay payment gateway
          </h2>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!s.razorpay_enabled}
              onChange={(e) => setS({ ...s, razorpay_enabled: e.target.checked })}
              className="accent-brand-maroon w-4 h-4"
              data-testid="razorpay-enable"
            />
            <span className="text-brand-soft">Enable live payments</span>
          </label>
        </div>
        <p className="text-xs text-brand-soft/60 leading-relaxed">
          Add your Razorpay <strong>Key ID</strong> and <strong>Key Secret</strong>. Until enabled, bookings use the mock-pay flow and you approve them manually from the Bookings tab. Find your keys at{" "}
          <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="underline text-brand-maroon">dashboard.razorpay.com → Settings → API Keys</a>.
        </p>
        <Input label="Razorpay Key ID" value={s.razorpay_key_id} onChange={(v) => setS({ ...s, razorpay_key_id: v })} placeholder="rzp_test_xxxxxxxxxx or rzp_live_xxxxxxxxxx" testId="razorpay-key-id" />
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-soft/60 mb-1">Razorpay Key Secret</div>
          <div className="relative">
            <input
              type={showSecret ? "text" : "password"}
              value={s.razorpay_key_secret || ""}
              onChange={(e) => setS({ ...s, razorpay_key_secret: e.target.value })}
              placeholder="••••••••••••••••"
              className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40"
              data-testid="razorpay-key-secret"
            />
            <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-soft/50 hover:text-brand-maroon">
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className="text-[11px] text-brand-soft/50 mt-1.5">Stored server-side. Never exposed to the public site.</div>
        </div>
        {s.razorpay_enabled && (!s.razorpay_key_id || !s.razorpay_key_secret) && (
          <div className="text-xs text-brand-maroon bg-brand-maroon/5 p-3 rounded-lg">
            ⚠ Live payments enabled but keys are missing. Add both Key ID and Secret to start accepting payments.
          </div>
        )}
      </div>

      {/* How It's Made */}
      <div className="bg-white rounded-2xl border border-brand-maroon/10 p-6 sm:p-8 space-y-4 mt-6">
        <div className="flex justify-between items-center">
          <h2 className="font-serif text-xl text-brand-soft">&quot;How It&apos;s Made&quot; sections</h2>
          <button onClick={addSection} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand-ivory hover:bg-brand-maroon/10"><Plus className="w-3 h-3" /> Add section</button>
        </div>
        {(s.how_its_made_sections || []).map((sec, i) => (
          <div key={i} className="bg-brand-ivory rounded-xl p-4 space-y-3">
            <div className="flex justify-between items-center">
              <div className="text-xs uppercase tracking-widest text-brand-soft/60">Section {i + 1}</div>
              <button onClick={() => removeSection(i)} className="text-xs text-red-700 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Remove</button>
            </div>
            <Input label="Eyebrow" value={sec.eyebrow} onChange={(v) => updSection(i, "eyebrow", v)} />
            <Input label="Title" value={sec.title} onChange={(v) => updSection(i, "title", v)} />
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-brand-soft/60 mb-1">Body</div>
              <textarea value={sec.body} onChange={(e) => updSection(i, "body", e.target.value)} rows={3} className="w-full bg-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" />
            </label>
            <ImageUploader label="Image" value={sec.image} onChange={(v) => updSection(i, "image", v)} />
          </div>
        ))}
      </div>

      <div className="mt-7 flex justify-end">
        <button onClick={save} className="btn-primary-furshala inline-flex items-center gap-2" data-testid="settings-save"><Save className="w-4 h-4" /> Save</button>
      </div>
    </div>
  );
}

const Input = ({ label, value, onChange, placeholder, testId }) => (
  <label className="block">
    <div className="text-xs uppercase tracking-widest text-brand-soft/60 mb-1">{label}</div>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" data-testid={testId} />
  </label>
);
